import { useState, useEffect } from "react";
import AdminLayout from "./layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Inbox, Plus, X, Save, Loader2, Trash2, Mail, Phone, Clock,
  AlertTriangle, ChevronRight, ArrowLeft, Building2,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Hospital {
  id: number;
  name: string;
  nhsTrustName: string;
  city: string;
  contracted: boolean;
  isActive: boolean;
}

interface Inbox {
  id: number;
  hospitalId: number;
  specialty: string;
  displayName: string;
  email: string;
  phone?: string | null;
  hours?: string | null;
  sla?: string | null;
  isActive: boolean;
}

const ALL_SPECIALTIES = [
  "rheumatology", "dermatology", "oncology", "endocrinology",
  "gastroenterology", "neurology", "cardiology", "respiratory",
  "nephrology", "urology", "haematology", "ophthalmology",
  "orthopaedics", "paediatrics", "psychiatry", "radiology",
  "general_surgery", "vascular", "obstetrics_gynaecology",
  "pain_management", "palliative_care",
];

function InboxRow({ inbox, onDelete, onUpdate }: { inbox: Inbox; onDelete: () => void; onUpdate: (inbox: Partial<Inbox>) => void }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(inbox);

  const handleSave = async () => {
    try {
      await fetch(`${BASE}/api/admin/content/hospitals/${inbox.hospitalId}/inboxes/${inbox.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      onUpdate(form);
      setEditing(false);
    } catch {}
  };

  if (editing) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Specialty</Label>
            <select
              value={form.specialty}
              onChange={e => setForm({ ...form, specialty: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border rounded-lg"
            >
              {ALL_SPECIALTIES.map(s => (
                <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Display name</Label>
            <Input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={form.phone ?? ""} onChange={e => setForm({ ...form, phone: e.target.value || null })} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Hours</Label>
            <Input value={form.hours ?? ""} onChange={e => setForm({ ...form, hours: e.target.value || null })} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">SLA</Label>
            <Input value={form.sla ?? ""} onChange={e => setForm({ ...form, sla: e.target.value || null })} className="text-sm" placeholder="e.g. 4 hours" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:bg-slate-50">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
          <Inbox className="h-4 w-4 text-indigo-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm text-slate-900">{inbox.displayName}</p>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-200 capitalize">
              {inbox.specialty?.replace(/_/g, " ")}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            {inbox.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {inbox.email}</span>}
            {inbox.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {inbox.phone}</span>}
            {inbox.hours && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {inbox.hours}</span>}
            {inbox.sla && <span className="text-teal-600 font-medium">SLA: {inbox.sla}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-xs">Edit</Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-xs text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

export default function AdminInboxes() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newInbox, setNewInbox] = useState<Partial<Inbox>>({ specialty: "rheumatology", isActive: true });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${BASE}/api/directory/hospitals`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((data: Hospital[]) => setHospitals(data))
      .catch(() => setError("Failed to load hospitals"));
  }, []);

  const loadInboxes = async (hospitalId: number) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/admin/content/hospitals/${hospitalId}/inboxes`, { credentials: "include" });
      if (res.ok) setInboxes(await res.json());
    } catch {}
    setLoading(false);
  };

  const handleSelectHospital = (h: Hospital) => {
    setSelectedHospital(h);
    loadInboxes(h.id);
    setAdding(false);
    setError(null);
  };

  const handleAddInbox = async () => {
    if (!selectedHospital || !newInbox.specialty || !newInbox.displayName) return;
    try {
      const res = await fetch(`${BASE}/api/admin/content/hospitals/${selectedHospital.id}/inboxes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newInbox),
      });
      if (res.ok) {
        const created = await res.json();
        setInboxes([...inboxes, created]);
        setNewInbox({ specialty: "rheumatology", isActive: true });
        setAdding(false);
      }
    } catch {}
  };

  const handleDeleteInbox = async (inbox: Inbox) => {
    if (!confirm(`Delete ${inbox.displayName}?`)) return;
    try {
      await fetch(`${BASE}/api/admin/content/hospitals/${inbox.hospitalId}/inboxes/${inbox.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setInboxes(inboxes.filter(i => i.id !== inbox.id));
    } catch {}
  };

  return (
    <AdminLayout title="Inbox Management">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-indigo-100 p-2.5 rounded-xl">
            <Inbox className="h-6 w-6 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Advice Line Inboxes</h2>
            <p className="text-sm text-slate-500">Manage which specialties are available at each contracted hospital</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 mb-4">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {!selectedHospital ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-500 mb-3">Select a contracted hospital to manage its inboxes:</p>
            {hospitals.map(h => (
              <button
                key={h.id}
                onClick={() => handleSelectHospital(h)}
                className="w-full text-left p-4 border border-slate-200 rounded-2xl hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-900">{h.name}</p>
                    <p className="text-xs text-slate-500">{h.nhsTrustName} · {h.city}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-indigo-400 transition-colors" />
              </button>
            ))}
            {hospitals.length === 0 && (
              <p className="text-slate-400 text-sm py-8 text-center">No contracted hospitals yet. Mark hospitals as contracted in the Hospitals section first.</p>
            )}
          </div>
        ) : (
          <div>
            <button
              onClick={() => { setSelectedHospital(null); setInboxes([]); }}
              className="text-slate-500 hover:text-slate-900 text-sm flex items-center gap-1 mb-4"
            >
              <ArrowLeft className="h-4 w-4" /> Back to hospitals
            </button>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-slate-600" />
                <div>
                  <p className="font-semibold text-slate-900">{selectedHospital.name}</p>
                  <p className="text-xs text-slate-500">{selectedHospital.nhsTrustName} · {selectedHospital.city}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-700">
                {inboxes.length} inbox{inboxes.length !== 1 ? "es" : ""}
              </p>
              {!adding && (
                <Button size="sm" onClick={() => setAdding(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add inbox
                </Button>
              )}
            </div>

            {adding && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3 mb-3">
                <p className="text-sm font-semibold text-indigo-800">New inbox</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Specialty</Label>
                    <select
                      value={newInbox.specialty ?? "rheumatology"}
                      onChange={e => setNewInbox({ ...newInbox, specialty: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border rounded-lg bg-white"
                    >
                      {ALL_SPECIALTIES.map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Display Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={newInbox.displayName ?? ""}
                      onChange={e => setNewInbox({ ...newInbox, displayName: e.target.value })}
                      placeholder="e.g. Rheumatology Advice Line"
                      className="text-sm bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                    <Input
                      value={newInbox.email ?? ""}
                      onChange={e => setNewInbox({ ...newInbox, email: e.target.value })}
                      placeholder="e.g. rheumatology@nhs.net"
                      className="text-sm bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={newInbox.phone ?? ""} onChange={e => setNewInbox({ ...newInbox, phone: e.target.value || null })} className="text-sm bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input value={newInbox.hours ?? ""} onChange={e => setNewInbox({ ...newInbox, hours: e.target.value || null })} placeholder="e.g. Mon-Fri 9am-5pm" className="text-sm bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs">SLA</Label>
                    <Input value={newInbox.sla ?? ""} onChange={e => setNewInbox({ ...newInbox, sla: e.target.value || null })} placeholder="e.g. 4 working hours" className="text-sm bg-white" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddInbox} className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAdding(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
            ) : (
              <div className="space-y-2">
                {inboxes.map(inbox => (
                  <InboxRow key={inbox.id} inbox={inbox} onDelete={() => handleDeleteInbox(inbox)} onUpdate={(partial) => {
                    setInboxes(inboxes.map(i => i.id === inbox.id ? { ...i, ...partial } : i));
                  }} />
                ))}
                {inboxes.length === 0 && !adding && (
                  <p className="text-slate-400 text-sm py-8 text-center">No inboxes configured for this hospital yet.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
